import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Tabs, 
  Tab,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import { 
  Code, 
  CheckCircle, 
  ErrorOutline, 
  Warning,
  ExpandMore,
  BugReport
} from '@material-ui/icons';
import { 
  ProjectBranchNamingReport, 
  BranchNamingCompliance,
  BranchNameViolation
} from '../types';
import { 
  getComplianceIcon, 
  getComplianceChip, 
  getComplianceColor,
  formatDate, 
  formatDateShort 
} from '../utils';
import { TabPanel } from '../shared';

// Branch naming overview cards component
interface BranchNamingOverviewCardsProps {
  namingReport: ProjectBranchNamingReport;
}

export const BranchNamingOverviewCards: React.FC<BranchNamingOverviewCardsProps> = ({ namingReport }) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Code style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3' }}>
            {namingReport.totalBranches}
          </Typography>
          <Typography variant="body2" color="textSecondary">Total Branches</Typography>
          <Typography variant="caption" style={{ color: namingReport.violatingBranches > 0 ? '#f44336' : '#4caf50' }}>
            {namingReport.violatingBranches} violations
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CheckCircle style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {namingReport.overallComplianceRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">Compliance Rate</Typography>
          <Typography variant="caption" color="textSecondary">
            {namingReport.compliantRepositories} repos compliant
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <ErrorOutline style={{ fontSize: 40, color: '#f44336', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#f44336' }}>
            {namingReport.violatingBranches}
          </Typography>
          <Typography variant="body2" color="textSecondary">Violating Branches</Typography>
          <Typography variant="caption" color="textSecondary">
            Need renaming
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <BugReport style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#ff9800' }}>
            {namingReport.summary.violationsByType.length}
          </Typography>
          <Typography variant="body2" color="textSecondary">Violation Types</Typography>
          <Typography variant="caption" color="textSecondary">
            Identified patterns
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Repository compliance list component
interface RepositoryNamingListProps {
  repositoryCompliance: BranchNamingCompliance[];
  onRepositorySelect: (repository: BranchNamingCompliance) => void;
}

export const RepositoryNamingList: React.FC<RepositoryNamingListProps> = ({ 
  repositoryCompliance, 
  onRepositorySelect 
}) => (
  <Grid container spacing={3}>
    {repositoryCompliance.map((repo, index) => (
      <Grid item xs={12} md={6} lg={4} key={index}>
        <Card 
          elevation={1} 
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onRepositorySelect(repo)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ marginBottom: '4px' }}>
                  {repo.repositoryName}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  Last checked: {formatDateShort(repo.lastChecked)}
                </Typography>
              </div>
              {getComplianceIcon(repo.complianceStatus)}
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              {getComplianceChip(repo.complianceStatus)}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Total Branches</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {repo.totalBranches}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Violations</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repo.violatingBranches > 0 ? '#f44336' : '#4caf50' }}>
                  {repo.violatingBranches}
                </Typography>
              </Grid>
            </Grid>

            {repo.summary.hasUppercaseViolations && (
              <div style={{ marginTop: '8px' }}>
                <Chip 
                  size="small" 
                  label="UPPERCASE Issues"
                  style={{ backgroundColor: '#f44336', color: 'white', fontSize: '0.7em' }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Common violations component
interface CommonViolationsProps {
  violationPatterns: ProjectBranchNamingReport['summary']['commonViolationPatterns'];
  totalBranches: number;
}

export const CommonViolations: React.FC<CommonViolationsProps> = ({ violationPatterns, totalBranches }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Most Common Naming Violations
    </Typography>
    {violationPatterns.map((pattern: ProjectBranchNamingReport['summary']['commonViolationPatterns'][number], index: number) => (
      <Card key={index} style={{ marginBottom: '12px' }} elevation={1}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {getViolationTypeDisplayName(pattern.pattern)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Chip 
                size="small" 
                label={`${pattern.count} branches`}
                style={{ backgroundColor: getSeverityColor(pattern.pattern), color: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography variant="body2" color="textSecondary">
                {pattern.affectedRepositories} repos
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="textSecondary">
                {((pattern.count / totalBranches) * 100).toFixed(1)}% of branches
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Affected repositories: {pattern.repositories.slice(0, 3).join(', ')}
                {pattern.repositories.length > 3 && ` and ${pattern.repositories.length - 3} more...`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ))}
    {violationPatterns.length === 0 && (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircle style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Naming Violations Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All branch names follow the naming standards
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Top violating repositories component
interface TopViolatingRepositoriesProps {
  repositories: ProjectBranchNamingReport['summary']['repositoriesWithMostViolations'];
}

export const TopViolatingRepositories: React.FC<TopViolatingRepositoriesProps> = ({ repositories }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Repositories with Most Violations
    </Typography>
    {repositories.map((repo: ProjectBranchNamingReport['summary']['repositoriesWithMostViolations'][number], index: number) => (
      <Card key={index} style={{ marginBottom: '12px' }} elevation={1}>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              {repo.repositoryName}
            </Typography>
            <Chip 
              size="small" 
              label={`${repo.violationCount} violations`}
              style={{ backgroundColor: '#f44336', color: 'white' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {repo.totalBranches} total branches
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {repo.violationRate.toFixed(1)}% violation rate
            </Typography>
          </div>
          <LinearProgress 
            variant="determinate" 
            value={repo.violationRate}
            style={{ 
              marginTop: '8px',
              backgroundColor: '#f0f0f0',
            }}
            color="secondary"
          />
        </CardContent>
      </Card>
    ))}
    {repositories.length === 0 && (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircle style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            All Repositories Compliant
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No repositories have branch naming violations
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Summary report component
interface NamingSummaryReportProps {
  namingReport: ProjectBranchNamingReport;
}

export const NamingSummaryReport: React.FC<NamingSummaryReportProps> = ({ namingReport }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Compliance Summary
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Repositories:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {namingReport.totalRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Compliant:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                {namingReport.compliantRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Non-Compliant:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                {namingReport.nonCompliantRepositories}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Unknown Status:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#9e9e9e' }}>
                {namingReport.unknownRepositories}
              </Typography>
            </div>
          </div>
          <LinearProgress 
            variant="determinate" 
            value={namingReport.overallComplianceRate}
            style={{ 
              marginTop: '16px',
              backgroundColor: '#f0f0f0',
            }}
          />
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
            Overall Compliance Rate: {namingReport.overallComplianceRate.toFixed(1)}%
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Branch Statistics
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Branches:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {namingReport.totalBranches}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Violating Branches:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#f44336' }}>
                {namingReport.violatingBranches}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Compliant Branches:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                {namingReport.totalBranches - namingReport.violatingBranches}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Last Updated:</Typography>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {formatDateShort(namingReport.lastUpdated)}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Branch naming tabs component
interface BranchNamingTabsProps {
  namingReport: ProjectBranchNamingReport;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  onRepositorySelect: (repository: BranchNamingCompliance) => void;
}

export const BranchNamingTabs: React.FC<BranchNamingTabsProps> = ({ 
  namingReport, 
  tabValue, 
  onTabChange, 
  onRepositorySelect 
}) => (
  <Card>
    <Tabs
      value={tabValue}
      onChange={onTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Repository Compliance" />
      <Tab label="Common Violations" />
      <Tab label="Top Violators" />
      <Tab label="Summary Report" />
    </Tabs>

    <TabPanel value={tabValue} index={0}>
      <RepositoryNamingList 
        repositoryCompliance={namingReport.repositoryCompliance}
        onRepositorySelect={onRepositorySelect}
      />
    </TabPanel>

    <TabPanel value={tabValue} index={1}>
      <CommonViolations 
        violationPatterns={namingReport.summary.commonViolationPatterns}
        totalBranches={namingReport.totalBranches}
      />
    </TabPanel>

    <TabPanel value={tabValue} index={2}>
      <TopViolatingRepositories 
        repositories={namingReport.summary.repositoriesWithMostViolations}
      />
    </TabPanel>

    <TabPanel value={tabValue} index={3}>
      <NamingSummaryReport namingReport={namingReport} />
    </TabPanel>
  </Card>
);

// Branch naming detail view component
interface BranchNamingDetailProps {
  repository: BranchNamingCompliance;
}

export const BranchNamingDetail: React.FC<BranchNamingDetailProps> = ({ repository }) => (
  <div>
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
              {repository.repositoryName}
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
              Branch Naming Standards Compliance Details
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last checked: {formatDate(repository.lastChecked)}
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getComplianceIcon(repository.complianceStatus)}
            {getComplianceChip(repository.complianceStatus)}
          </div>
        </div>
      </CardContent>
    </Card>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Branch Statistics
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Total Branches:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {repository.totalBranches}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Compliant Branches:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {repository.compliantBranches}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Violating Branches:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.violatingBranches > 0 ? '#f44336' : '#4caf50' }}>
                  {repository.violatingBranches}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Compliance Rate:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {repository.totalBranches > 0 ? ((repository.compliantBranches / repository.totalBranches) * 100).toFixed(1) : 0}%
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Violation Summary
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Uppercase Issues:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.summary.hasUppercaseViolations ? '#f44336' : '#4caf50' }}>
                  {repository.summary.hasUppercaseViolations ? 'Found (✗)' : 'None (✓)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Special Character Issues:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.summary.hasSpecialCharViolations ? '#f44336' : '#4caf50' }}>
                  {repository.summary.hasSpecialCharViolations ? 'Found (✗)' : 'None (✓)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Length Issues:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: repository.summary.hasLengthViolations ? '#f44336' : '#4caf50' }}>
                  {repository.summary.hasLengthViolations ? 'Found (✗)' : 'None (✓)'}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Most Common Violation:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {repository.summary.mostCommonViolationType 
                    ? getViolationTypeDisplayName(repository.summary.mostCommonViolationType)
                    : 'None'
                  }
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Detailed Violations */}
      {repository.violations.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
                Branch Naming Violations ({repository.violations.length})
              </Typography>
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {repository.violations.map((violation: BranchNameViolation, index: number) => (
                  <Accordion key={index} style={{ marginBottom: '8px' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Code style={{ color: '#666', fontSize: 18 }} />
                          <Typography variant="body1" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {violation.branchName}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Chip 
                            size="small" 
                            label={getViolationTypeDisplayName(violation.violationType)}
                            style={{ 
                              backgroundColor: getSeverityColor(violation.violationType), 
                              color: 'white' 
                            }}
                          />
                          <Chip 
                            size="small" 
                            label={violation.severity.toUpperCase()}
                            variant="outlined"
                            style={{ 
                              borderColor: getSeverityColor(violation.violationType),
                              color: getSeverityColor(violation.violationType)
                            }}
                          />
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div style={{ width: '100%' }}>
                        <Typography variant="body2" style={{ marginBottom: '8px' }}>
                          <strong>Issue:</strong> {violation.description}
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: '8px', color: '#4caf50' }}>
                          <strong>Suggestion:</strong> {violation.suggestion}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
                          Full reference: {violation.branchFullName}
                        </Typography>
                        {violation.positions.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            <Typography variant="caption" color="textSecondary">
                              Problem positions: {violation.positions.join(', ')}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* No Violations */}
      {repository.violations.length === 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent style={{ textAlign: 'center', padding: '60px' }}>
              <CheckCircle style={{ fontSize: 80, color: '#4caf50', marginBottom: '16px' }} />
              <Typography variant="h5" style={{ marginBottom: '8px', color: '#4caf50' }}>
                All Branch Names Compliant
              </Typography>
              <Typography variant="body1" color="textSecondary">
                This repository follows all branch naming standards. All {repository.totalBranches} branches have proper naming conventions.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  </div>
);

// Helper functions
const getViolationTypeDisplayName = (type: string): string => {
  const displayNames = {
    'uppercase_letters': 'Uppercase Letters',
    'special_characters': 'Special Characters',
    'excessive_length': 'Excessive Length',
    'consecutive_separators': 'Consecutive Separators',
    'leading_trailing_separators': 'Leading/Trailing Separators'
  };
  
  return displayNames[type as keyof typeof displayNames] || type;
};

const getSeverityColor = (type: string): string => {
  const severityColors = {
    'uppercase_letters': '#f44336', // High severity - red
    'special_characters': '#ff9800', // Medium severity - orange
    'excessive_length': '#ff9800', // Medium severity - orange
    'consecutive_separators': '#2196f3', // Low severity - blue
    'leading_trailing_separators': '#2196f3' // Low severity - blue
  };
  
  return severityColors[type as keyof typeof severityColors] || '#9e9e9e';
};